/**
 * CREATIVE DETONATOR - Phase 4: Harvest
 * Results display and idea management
 *
 * Features:
 * - Results rendering with format-specific cards
 * - Card selection (visual) with SelectionManager
 * - Idea saving (for export)
 * - Select all / Clear all
 * - Presentation mode integration
 * - Export buttons initialization
 * - Batch operations via ActionBar
 *
 * v73 - Uses shared renderIdeaCard from idea-card.js
 *       Eliminated ~275 lines of duplicate rendering code
 *       (renderIdeaCard, renderImageSection, renderGenerateButton)
 * v74 - Migrated to SelectionManager + ActionBar for batch operations
 */

// v73 - Uses shared idea card renderer (eliminated duplicate code)
// v74 - SelectionManager integration
import { GeneratorState, elements } from '../core/state.js';
// Import canvas module (self-registers to window)
import '../../../js/shared/idea-canvas.js';
import { goToPhase, resetGenerator } from '../core/navigation.js';
import { initCopyPromptDropdown, openCopyDropdown } from '../ui/copy-dropdown.js';
// Shared idea card renderer - single source of truth
import { renderIdeaCard } from '../ui/idea-card.js';
import {
  initImageGeneration,
  generateForSelected,
  initImagePreviewModal,
  getIdeaImageUrl
} from '../features/image-generation.js';
import { initImageEdit } from '../features/image-edit.js';

// Selection with ActionBar
import { harvestSelection } from '../../../js/core/selection-manager.js';
import { initActionBar } from '../../../js/shared/action-bar.js';

// XSS prevention
import { escapeHtml } from '../../../js/core/sanitize.js';

// Error handling
import { showError } from '../../../js/core/error-handler.js';

// Export dropdown - import the module to ensure it loads
import '../features/export.js';

// Logger
import { createLogger } from '../../../js/core/logger.js';
const log = createLogger('Phase4');

// Access globals from main.js
const CategoryColors = window.CategoryColors;

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize Phase 4 (Harvest)
 * Sets up results display, buttons, and event handlers
 */
export function initPhase4() {
  // Update brief display with multi-format support
  const { product, benefit, formats } = GeneratorState.brief;
  const briefText = document.getElementById('harvest-brief-text');
  if (briefText) {
    const formatDisplay = formats.map(f => f.toUpperCase()).join(' + ');
    briefText.textContent = `${product} → ${benefit} → [${formatDisplay}]`;
  }

  // V5: Update header with brand color and format breakdown
  updateHarvestHeader();

  // V5: Update brand section in brief
  updateHarvestBrandSection();

  renderResults();

  // Initialize copy prompt dropdown for ALL formats
  initCopyPromptDropdown();

  // Initialize image generation module
  initImageGeneration();

  // Initialize image preview modal
  initImagePreviewModal();

  // Initialize Image Edit module
  initImageEdit();

  // Initialize presentation mode button
  initPresentationMode();

  // Initialize canvas for inline detail view
  if (window.IdeaCanvas) {
    window.IdeaCanvas.init('harvest-results', 'canvas-view');
  }

  // New session button (OR return to Dashboard in Quick Launch mode)
  const newSessionBtn = document.getElementById('btn-new-session');
  if (newSessionBtn) {
    // Check if we're in Quick Launch return mode
    if (GeneratorState.quickLaunchReturnUrl) {
      // Transform into "Return to Dashboard" button
      newSessionBtn.innerHTML = '<img src="/assets/icons/icon_0030_happy-mac.png" class="pixel-icon invert" alt=""> RETURN TO DASHBOARD';
      newSessionBtn.title = 'Return to Dashboard';
      newSessionBtn.addEventListener('click', () => {
        // Navigate back to Dashboard
        window.location.href = GeneratorState.quickLaunchReturnUrl;
      });

      log.debug('Quick Launch mode: Return to Dashboard button enabled');
    } else {
      // Normal new session behavior
      newSessionBtn.addEventListener('click', () => {
        resetGenerator();
        goToPhase(1);
      });
    }
  }

  // Export TXT button
  if (elements.btnExportTxt) {
    elements.btnExportTxt.addEventListener('click', () => {
      if (typeof window.exportIdeasTxt === 'function') {
        window.exportIdeasTxt();
      }
    });
  }

  // Export PDF button
  if (elements.btnExportPdf) {
    elements.btnExportPdf.addEventListener('click', () => {
      if (typeof window.exportIdeasPdf === 'function') {
        window.exportIdeasPdf();
      }
    });
  }

  // Initialize export dropdown (replaces old download button)
  // Use window global since module is imported above
  if (typeof window.initExportDropdown === 'function') {
    window.initExportDropdown();
  }

  // =========================================================================
  // BATCH SELECTION - Initialize SelectionManager and ActionBar
  // =========================================================================
  initHarvestSelection();
}

// ============================================================================
// V5: HARVEST HEADER (Title color + Format breakdown)
// ============================================================================

/**
 * Update the harvest header
 * - Shows "YOUR {BRAND} IDEAS" with brand name highlighted in brand color
 * - Shows format breakdown as simple text (e.g., "Here's what we created: 7 Prints, 3 Videos")
 */
function updateHarvestHeader() {
  const brand = GeneratorState.sessionBrand || GeneratorState.currentBrand;

  // Get product/brand name to display
  const product = GeneratorState.selectedProduct ||
                  GeneratorState.sessionProduct ||
                  GeneratorState.brief?.productData;
  const displayName = product?.name || GeneratorState.brief?.product || brand?.name;

  // Shell color mapping (vibrant colors for highlight)
  const SHELL_COLORS = window.SHELL_COLORS || {
    slate: '#64748b',
    orange: '#f97316',
    charcoal: '#374151',
    violet: '#8b5cf6',
    teal: '#14b8a6',
    blue: '#3b82f6',
    rose: '#f43f5e',
    amber: '#f59e0b',
    stone: '#78716c'
  };

  // Update title to "YOUR {BRAND} IDEAS" with brand highlight
  const titleEl = document.getElementById('harvest-title');
  if (titleEl) {
    if (displayName && brand) {
      // Use shell color for highlight (vibrant) - brand.colors[0] is often black
      const shellColor = SHELL_COLORS[brand.shellColor || brand.shell_color] || '#f97316'; // Default to orange
      titleEl.innerHTML = `YOUR <span class="harvest-title-brand" style="color: ${escapeHtml(shellColor)}">${escapeHtml(displayName.toUpperCase())}</span> IDEAS`;
    } else if (displayName) {
      // No brand, use neutral highlight
      titleEl.innerHTML = `YOUR <span class="harvest-title-brand" style="color: #666666">${escapeHtml(displayName.toUpperCase())}</span> IDEAS`;
    } else {
      titleEl.innerHTML = 'YOUR IDEAS';
    }
  }

  // Add/remove brand-loaded class to hide category colors when brand is present
  const harvestResults = document.getElementById('harvest-results');
  if (harvestResults) {
    if (brand) {
      harvestResults.classList.add('brand-loaded');
    } else {
      harvestResults.classList.remove('brand-loaded');
    }
  }

  // Update format breakdown as simple text
  const breakdownEl = document.getElementById('harvest-format-breakdown');
  if (breakdownEl) {
    // Count ideas per format
    const formatCounts = {};
    let total = 0;
    for (const group of GeneratorState.generatedIdeas) {
      for (const idea of group.ideas) {
        const format = (idea.format || 'unknown');
        // Capitalize first letter
        const formatName = format.charAt(0).toUpperCase() + format.slice(1).toLowerCase();
        formatCounts[formatName] = (formatCounts[formatName] || 0) + 1;
        total++;
      }
    }

    // Build simple text like "Here's what we created: 7 Prints, 3 Videos"
    const parts = Object.entries(formatCounts)
      .map(([format, count]) => `${count} ${format}${count > 1 ? 's' : ''}`)
      .join(', ');

    breakdownEl.textContent = `Here's what we created: ${parts}`;
  }
}

// ============================================================================
// V5: BRAND SECTION IN HARVEST BRIEF
// ============================================================================

/**
 * Update the brand section in harvest brief
 * Shows brand and product details when a brand was used in this session
 * Priority: sessionBrand (for reopened sessions) > currentBrand (for new sessions)
 */
function updateHarvestBrandSection() {
  const brandSection = document.getElementById('harvest-brief-brand');
  if (!brandSection) return;

  // Use session brand if reopening, otherwise current brand
  const brand = GeneratorState.sessionBrand || GeneratorState.currentBrand;

  // Get product info (from selectedProduct, sessionProduct, or brief.productData)
  const product = GeneratorState.selectedProduct ||
                  GeneratorState.sessionProduct ||
                  GeneratorState.brief?.productData;
  const productName = product?.name || GeneratorState.brief?.product;

  if (!brand && !productName) {
    // No brand or product loaded - hide section
    brandSection.classList.remove('visible');
    brandSection.innerHTML = '';
    return;
  }

  // Get shell color from global SHELL_COLORS
  const SHELL_COLORS = window.SHELL_COLORS || {
    slate: '#64748b',
    orange: '#f97316',
    charcoal: '#374151',
    violet: '#8b5cf6',
    teal: '#14b8a6',
    blue: '#3b82f6',
    rose: '#f43f5e',
    amber: '#f59e0b',
    stone: '#78716c'
  };

  const shellColor = brand ? (SHELL_COLORS[brand.shellColor || brand.shell_color] || '#64748b') : '#64748b';

  // Get brand colors (check both `colors` and `primary_colors` for compatibility)
  const colors = brand?.colors || brand?.primary_colors || [];

  // Build product line - only show if different from brand name
  const showProduct = productName && (!brand || productName.toLowerCase() !== brand.name.toLowerCase());

  // Build the mini brand card HTML
  brandSection.innerHTML = `
    <div class="harvest-brand-card" style="background: ${shellColor}">
      <span class="harvest-brand-led"></span>
      <div class="harvest-brand-info">
        ${brand ? `<span class="harvest-brand-name">${escapeHtml(brand.name)}</span>` : ''}
        ${brand?.industry ? `<span class="harvest-brand-industry">${escapeHtml(brand.industry)}</span>` : ''}
        ${showProduct ? `<span class="harvest-brand-product">${escapeHtml(productName)}</span>` : ''}
      </div>
      ${colors.length > 0 ? `
        <div class="harvest-brand-colors">
          ${colors.slice(0, 4).map(c => `<span class="harvest-brand-swatch" style="background: ${c}"></span>`).join('')}
        </div>
      ` : ''}
    </div>
  `;

  brandSection.classList.add('visible');
}

// Track if selection was already initialized to prevent duplicate event listeners
let selectionInitialized = false;
let actionBarInstance = null;

/**
 * Initialize harvest selection system
 * Uses ActionBar for batch operations (SELECT ALL, CLEAR, GENERATE IMAGES)
 * Syncs with GeneratorState.selectedIdeas for backwards compatibility
 */
function initHarvestSelection() {
  // Clear any previous selection state
  harvestSelection.clear();

  // Prevent duplicate initialization (e.g., when regenerating ideas)
  if (selectionInitialized) {
    log.debug('Harvest selection already initialized, skipping');
    return;
  }
  selectionInitialized = true;

  // Sync selection manager with GeneratorState.selectedIdeas for backwards compat
  // This ensures existing code (generateForSelected, saveAllSelected) still works
  harvestSelection.on('change', ({ selected }) => {
    GeneratorState.selectedIdeas = selected;
  });

  // Bind click-to-select on idea cards (uses event delegation)
  harvestSelection.bindToCards(
    '#harvest-results',
    '.idea-card',
    'data-id'
  );

  // Initialize ActionBar for Phase 4
  actionBarInstance = initActionBar({
    selection: harvestSelection,
    containerId: 'harvest-action-bar',
    getAllIds: getAllHarvestIdeaIds,
    onVisibilityChange: (isVisible) => {
      // Hide saved-bar when action bar is visible to avoid collision
      const savedBar = document.getElementById('saved-bar');
      if (savedBar) savedBar.style.display = isVisible ? 'none' : 'flex';
    },
    actions: [
      {
        id: 'generate',
        label: 'GENERATE IMAGES',
        icon: 'image',
        handler: async (selectedIds) => {
          await generateForSelected();
        }
      }
    ]
  });

  log.debug('Harvest selection initialized with ActionBar');
}

/**
 * Get all idea IDs from the current harvest session
 * @returns {string[]}
 */
function getAllHarvestIdeaIds() {
  const ideas = [];
  for (const group of GeneratorState.generatedIdeas || []) {
    for (const idea of group.ideas || []) {
      if (idea.id) ideas.push(idea.id);
    }
  }
  return ideas;
}

// ============================================================================
// RESULTS RENDERING
// ============================================================================

/**
 * Render all generated ideas to the results grid
 */
function renderResults() {
  const results = GeneratorState.generatedIdeas;
  let totalIdeas = 0;
  let ideaNumber = 0;

  log.debug(`Rendering ${results.length} trigger groups`);

  elements.harvestResults.innerHTML = results.map(group => {
    const color = CategoryColors[group.trigger.category];

    const ideasHtml = group.ideas.map(idea => {
      totalIdeas++;
      ideaNumber++;
      // Use shared renderer with options object
      return renderIdeaCard(idea, ideaNumber, {
        categoryColor: color,
        isSelected: GeneratorState.selectedIdeas.includes(idea.id),
        isSaved: GeneratorState.savedIdeas.includes(idea.id)
      });
    }).join('');

    return `
      <div class="trigger-result-group">
        <div class="trigger-result-header">
          <span class="trigger-result-code" style="border-color: ${escapeHtml(color)}; background-color: ${escapeHtml(color)}20">${escapeHtml(group.trigger.code)}</span>
          <span class="trigger-result-name">${escapeHtml(group.trigger.name).toUpperCase()}</span>
        </div>
        <div class="trigger-result-question">"${escapeHtml(group.trigger.question)}"</div>
        <div class="ideas-grid">
          ${ideasHtml}
        </div>
      </div>
    `;
  }).join('');

  // Update idea count if element exists
  if (elements.ideaCount) {
    elements.ideaCount.textContent = totalIdeas;
  }

  // Add save handlers to buttons
  elements.harvestResults.querySelectorAll('.idea-save-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleSaveIdea(btn.dataset.id, btn);
    });
  });

  // Add copy handlers to copy buttons
  elements.harvestResults.querySelectorAll('.idea-copy-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      copyImagePrompt(btn.dataset.id);
    });
  });

  // NOTE: Card click-to-select is handled by harvestSelection.bindToCards() in initHarvestSelection()
  // Using event delegation instead of individual handlers - no duplicate binding

  // Add canvas view button handlers (small icon in header)
  elements.harvestResults.querySelectorAll('.btn-canvas-view').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const ideaId = btn.dataset.id;

      // Find the idea in GeneratorState
      let idea = null;
      for (const group of GeneratorState.generatedIdeas) {
        const found = group.ideas.find(i => i.id === ideaId);
        if (found) {
          idea = found;
          break;
        }
      }

      if (idea && window.IdeaCanvas) {
        window.IdeaCanvas.open(idea);
      }
    });
  });

  // Add VIEW IDEA button handlers (large button at bottom)
  elements.harvestResults.querySelectorAll('.btn-view-idea-large').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const ideaId = btn.dataset.id;

      // Find the idea in GeneratorState
      let idea = null;
      for (const group of GeneratorState.generatedIdeas) {
        const found = group.ideas.find(i => i.id === ideaId);
        if (found) {
          idea = found;
          break;
        }
      }

      if (idea && window.IdeaCanvas) {
        window.IdeaCanvas.open(idea);
      }
    });
  });
}

// ============================================================================
// CARD ACTIONS
// NOTE: Card rendering is now handled by shared idea-card.js module
// ============================================================================

/**
 * Open copy prompt dropdown for an idea
 * @param {string} ideaId - The idea ID
 */
function copyImagePrompt(ideaId) {
  openCopyDropdown(ideaId);
}

/**
 * Toggle SELECTION (visual highlight on card click)
 * Now uses harvestSelection for unified state management
 * @param {string} ideaId - The idea ID
 */
function toggleSelectIdea(ideaId) {
  const isSelected = harvestSelection.toggle(ideaId);
  const card = document.querySelector(`.idea-card[data-id="${ideaId}"]`);

  if (card) {
    card.classList.toggle('selected', isSelected);
  }

  updateIdeaSelectionCount();
}

/**
 * Toggle SAVE (for export - SAVE button only)
 * Persists save state to database via API
 * @param {string} ideaId - The idea ID
 * @param {HTMLElement} button - The save button element
 */
async function toggleSaveIdea(ideaId, button) {
  const index = GeneratorState.savedIdeas.indexOf(ideaId);
  const isSaving = index === -1;  // Will be saving if not currently in array

  // Optimistic UI update
  if (isSaving) {
    GeneratorState.savedIdeas.push(ideaId);
    if (button) button.classList.add('saved');
  } else {
    GeneratorState.savedIdeas.splice(index, 1);
    if (button) button.classList.remove('saved');
  }
  updateSavedBar();

  // Persist to database via API
  try {
    if (window.api) {
      if (isSaving) {
        await window.api.saveIdea(ideaId);
      } else {
        await window.api.unsaveIdea(ideaId);
      }
      log.debug(`Idea ${ideaId} save state persisted: ${isSaving}`);
    }
  } catch (error) {
    log.error('Failed to persist save state:', error);
    // Revert UI on failure
    if (isSaving) {
      const idx = GeneratorState.savedIdeas.indexOf(ideaId);
      if (idx > -1) GeneratorState.savedIdeas.splice(idx, 1);
      if (button) button.classList.remove('saved');
    } else {
      GeneratorState.savedIdeas.push(ideaId);
      if (button) button.classList.add('saved');
    }
    updateSavedBar();
  }
}

/**
 * Update idea selection count display
 */
function updateIdeaSelectionCount() {
  // Phase 4 idea selection count (for future UI display)
  // Currently just tracks internally
}

/**
 * Select all ideas (visual selection)
 * Now uses harvestSelection for unified state management
 */
function selectAllIdeas() {
  const allIds = getAllHarvestIdeaIds();
  harvestSelection.selectAll(allIds);

  // Update all cards visually
  elements.harvestResults.querySelectorAll('.idea-card').forEach(card => {
    card.classList.add('selected');
  });

  updateIdeaSelectionCount();
}

/**
 * Clear all selections (visual only)
 * Now uses harvestSelection for unified state management
 */
function clearAllIdeas() {
  harvestSelection.clear();

  // Update all cards visually
  elements.harvestResults.querySelectorAll('.idea-card').forEach(card => {
    card.classList.remove('selected');
  });

  updateIdeaSelectionCount();
}

/**
 * Save all selected ideas
 */
export function saveAllSelected() {
  GeneratorState.selectedIdeas.forEach(ideaId => {
    if (!GeneratorState.savedIdeas.includes(ideaId)) {
      GeneratorState.savedIdeas.push(ideaId);
      const btn = document.querySelector(`.idea-save-btn[data-id="${ideaId}"]`);
      if (btn) btn.classList.add('saved');
    }
  });
  updateSavedBar();
}

/**
 * Update the saved bar display (no-op - saved counter removed)
 */
export function updateSavedBar() {
  // No-op - saved ideas counter UI has been removed
  // Function kept for compatibility with existing calls
}

// ============================================================================
// PRESENTATION MODE
// ============================================================================

/**
 * Initialize presentation mode button
 */
function initPresentationMode() {
  const btn = document.getElementById('btn-presentation-mode');
  if (!btn) return;

  btn.addEventListener('click', () => {
    openPresentationMode();
  });
}

/**
 * Open presentation mode with all generated ideas
 * Maps the internal idea format to PresentationMode expected format
 */
function openPresentationMode() {
  // Flatten all ideas from all trigger groups
  const ideas = [];

  GeneratorState.generatedIdeas.forEach(group => {
    // Get category color from CategoryColors map
    const categoryColor = CategoryColors[group.trigger.category] || '#000000';

    group.ideas.forEach(idea => {
      // Get image URL from preloaded or generated images
      const imageUrl = getIdeaImageUrl(idea.id);

      ideas.push({
        id: idea.id,
        format: idea.format || 'print',
        title: idea.title,
        idea: idea.idea || '',  // V2 NEW: Core creative insight
        headline: idea.headline || '',
        headlineLabel: idea.headlineLabel || 'HEADLINE',
        subline: idea.subline || '',  // V2 NEW: Subline
        text: idea.text || '',
        triggerCode: group.trigger.code,
        triggerName: group.trigger.name,
        whyItWorks: idea.whyItWorks || idea.mechanism || '',
        categoryColor: categoryColor,
        imageUrl: imageUrl  // Generated image URL (if available)
      });
    });
  });

  if (ideas.length === 0) {
    showError('No ideas to preview. Generate some ideas first!', { context: 'presentation mode' });
    return;
  }

  // Check if PresentationMode is available
  if (typeof PresentationMode === 'undefined') {
    log.error('PresentationMode not loaded');
    showError('Presentation mode is not available. Please refresh the page.', { context: 'presentation mode' });
    return;
  }

  // Create and open presentation
  const presentation = new PresentationMode({
    onClose: () => {
      log.debug('Presentation closed');
    },
    onSave: (idea, isSaved) => {
      // Sync with saved ideas state
      const index = GeneratorState.savedIdeas.indexOf(idea.id);
      if (isSaved && index === -1) {
        GeneratorState.savedIdeas.push(idea.id);
        // Update the save button in the UI
        const btn = document.querySelector(`.idea-save-btn[data-id="${idea.id}"]`);
        if (btn) btn.classList.add('saved');
      } else if (!isSaved && index > -1) {
        GeneratorState.savedIdeas.splice(index, 1);
        const btn = document.querySelector(`.idea-save-btn[data-id="${idea.id}"]`);
        if (btn) btn.classList.remove('saved');
      }
      updateSavedBar();
    }
  });

  presentation.open(ideas, 0);
}

// ============================================================================
// GLOBAL EXPORTS FOR COMPATIBILITY
// ============================================================================

// Expose to window for compatibility
window.initPhase4 = initPhase4;
window.updateSavedBar = updateSavedBar;
window.saveAllSelected = saveAllSelected;
